import type { AuthError, Provider } from "@supabase/supabase-js"
import Constants from "expo-constants"
import { Platform } from "react-native"

export type SupportedOAuthProvider = Extract<Provider, "apple" | "google" | "linkedin_oidc">
export type AuthFlowMode = "signin" | "signup" | "magic" | "reset"
export type AuthValidationInput = {
  mode: AuthFlowMode
  email: string
  password?: string
  confirmPassword?: string
}
export type AuthValidationErrors = {
  email?: string
  password?: string
  confirmPassword?: string
}

declare const process: {
  env: Record<string, string | undefined>
}

const DEV_AUTH_EMAIL = "dev@anuka.pro"
const TEST_AUTH_EMAIL = "test@anuka.pro"
const DEFAULT_DEV_AUTH_PASSWORD = "testanuka"

function readExtraFlag(name: string) {
  const extra = Constants.expoConfig?.extra as Record<string, unknown> | undefined
  const value =
    name === "DISABLE_DEV_AUTO_LOGIN"
      ? extra?.disableDevAutoLogin
      : name === "E2E_DEV_PASSTHROUGH"
        ? extra?.e2eDevPassthrough
        : name === "DISABLE_E2E_PASSTHROUGH"
          ? extra?.disableE2ePassthrough
          : name === "E2E_AUTH_FLOW_STUB"
            ? extra?.e2eAuthFlowStub
            : undefined

  return typeof value === "string" ? value === "true" : value === true
}

function readRuntimeFlag(name: string) {
  const publicEnvValue =
    name === "DISABLE_DEV_AUTO_LOGIN"
      ? process.env.EXPO_PUBLIC_DISABLE_DEV_AUTO_LOGIN
      : name === "E2E_DEV_PASSTHROUGH"
        ? process.env.EXPO_PUBLIC_E2E_DEV_PASSTHROUGH
        : name === "DISABLE_E2E_PASSTHROUGH"
          ? process.env.EXPO_PUBLIC_DISABLE_E2E_PASSTHROUGH
          : name === "E2E_AUTH_FLOW_STUB"
            ? process.env.EXPO_PUBLIC_E2E_AUTH_FLOW_STUB
            : undefined
  const envValue = process.env[name] ?? publicEnvValue
  if (envValue !== undefined) return envValue === "true"

  if (readExtraFlag(name)) return true

  if (Platform.OS === "web" && typeof window !== "undefined") {
    const runtime = window as unknown as Record<string, unknown>
    const localBrowser =
      window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1"

    const queryFlag =
      name === "E2E_AUTH_FLOW_STUB" &&
      localBrowser &&
      new URLSearchParams(window.location.search).get("e2eAuthFlowStub") === "true"
    if (queryFlag) return true

    return runtime[name] === true || runtime[`EXPO_PUBLIC_${name}`] === true
  }

  return false
}

export function normalizeAuthEmail(email: string) {
  return email.trim().toLowerCase()
}

export function validateAuthFlow(input: AuthValidationInput): AuthValidationErrors {
  const errors: AuthValidationErrors = {}
  const email = normalizeAuthEmail(input.email)

  if (!email) {
    errors.email = "Email is required."
  } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
    errors.email = "Please enter a valid email address."
  }

  if (input.mode !== "magic" && input.mode !== "reset") {
    const password = input.password ?? ""
    if (!password) {
      errors.password = "Password is required."
    } else if (password.length < 6) {
      errors.password = "Password must be at least 6 characters."
    }

    if (input.mode === "signup") {
      const confirmPassword = input.confirmPassword ?? ""
      if (!confirmPassword) {
        errors.confirmPassword = "Please confirm your password."
      } else if (confirmPassword !== password) {
        errors.confirmPassword = "Passwords do not match."
      }
    }
  }

  return errors
}

export function hasAuthValidationErrors(errors: AuthValidationErrors) {
  return Boolean(errors.email || errors.password || errors.confirmPassword)
}

export function getDevelopmentAuthCredentials() {
  return {
    email: process.env.EXPO_PUBLIC_DEV_AUTH_EMAIL ?? DEV_AUTH_EMAIL,
    password: process.env.EXPO_PUBLIC_DEV_AUTH_PASSWORD ?? DEFAULT_DEV_AUTH_PASSWORD,
  }
}

export function getTestAuthCredentials() {
  return {
    email: process.env.EXPO_PUBLIC_TEST_AUTH_EMAIL ?? TEST_AUTH_EMAIL,
    password: process.env.EXPO_PUBLIC_TEST_AUTH_PASSWORD ?? DEFAULT_DEV_AUTH_PASSWORD,
  }
}

export function isDevelopmentAutoLoginDisabled() {
  return readRuntimeFlag("DISABLE_DEV_AUTO_LOGIN")
}

export function isE2EDevPassthroughEnabled() {
  return readRuntimeFlag("E2E_DEV_PASSTHROUGH") && !readRuntimeFlag("DISABLE_E2E_PASSTHROUGH")
}

export function isE2EAuthFlowStubEnabled() {
  return readRuntimeFlag("E2E_AUTH_FLOW_STUB") && !readRuntimeFlag("DISABLE_E2E_PASSTHROUGH")
}

export function getOAuthCodeFromUrl(url: string | null | undefined) {
  if (!url) return null

  const queryOrHash = url.split(/[?#]/).slice(1).join("&")
  const params = new URLSearchParams(queryOrHash)

  return params.get("code")
}

export function formatAuthMessage(error: unknown) {
  const rawMessage =
    error instanceof Error
      ? error.message
      : typeof error === "object" && error && "message" in error
        ? String((error as AuthError).message)
        : String(error || "")
  const normalized = rawMessage.toLowerCase()

  if (!rawMessage) return "Authentication failed. Please try again."
  if (normalized.includes("invalid login credentials")) {
    return "Invalid email or password. Please check your credentials and try again."
  }
  if (normalized.includes("email not confirmed")) {
    return "Please verify your email before signing in."
  }
  if (normalized.includes("already registered") || normalized.includes("already exists")) {
    return "An account with this email already exists. Please sign in instead."
  }
  if (normalized.includes("rate limit") || normalized.includes("too many")) {
    return "Too many attempts. Please wait a moment before trying again."
  }
  if (normalized.includes("network") || normalized.includes("fetch")) {
    return "Network error. Please check your connection and try again."
  }
  if (
    normalized.includes("popup_closed_by_user") ||
    normalized.includes("cancelled") ||
    normalized.includes("user_cancelled")
  ) {
    return "Sign-in was cancelled."
  }
  if (normalized.includes("popup_blocked") || normalized.includes("blocked_popup")) {
    return "Pop-up was blocked. Please allow pop-ups and try again."
  }
  if (normalized.includes("invalid_client") || normalized.includes("unauthorized_client")) {
    return "Authentication provider configuration needs attention. Please contact support."
  }
  if (normalized.includes("redirect_uri_mismatch")) {
    return "Authentication redirect configuration needs attention. Please contact support."
  }
  if (normalized.includes("provider") && normalized.includes("disabled")) {
    return "This sign-in provider is not enabled yet. Please use another method."
  }

  return rawMessage
}

export function getOAuthProviderLabel(provider: SupportedOAuthProvider) {
  if (provider === "linkedin_oidc") return "LinkedIn"
  if (provider === "apple") return "Apple"
  return "Google"
}

export function isAppleOAuthConfigured() {
  return process.env.EXPO_PUBLIC_AUTH_APPLE_ENABLED === "true"
}

export function getOAuthProviderUnavailableMessage(provider: SupportedOAuthProvider) {
  if (provider === "apple") {
    return "Apple sign-in is not available yet. Please use email, Google, or LinkedIn while Apple provider configuration is completed."
  }

  return `${getOAuthProviderLabel(provider)} sign-in is not available. Please use another method.`
}

export function getOAuthQueryParams(provider: SupportedOAuthProvider) {
  if (provider === "linkedin_oidc") {
    return {
      response_type: "code",
      scope: "openid profile email",
    }
  }

  if (provider === "apple") {
    return {
      response_type: "code",
      scope: "name email",
    }
  }

  return {
    access_type: "offline",
    prompt: "consent",
    response_type: "code",
    scope: "openid email profile",
  }
}

export function getOAuthSignInOptions(provider: SupportedOAuthProvider, redirectTo: string) {
  return {
    provider,
    options: {
      redirectTo,
      skipBrowserRedirect: Platform.OS !== "web",
      queryParams: getOAuthQueryParams(provider),
    },
  }
}
